import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function APIDocumentation() {
  const endpoints = [
    {
      category: 'Authentication',
      routes: [
        {
          method: 'POST',
          path: '/api/auth/login',
          description: 'Login to get authentication token',
          body: { email: 'user@example.com', password: 'password' },
          response: { token: 'demo_token_xxx', user: { id: 'xxx', email: 'user@example.com' } },
        },
      ],
    },
    {
      category: 'Products',
      routes: [
        {
          method: 'GET',
          path: '/api/products',
          description: 'Get all products (customer + internal)',
          params: 'warehouse_id (optional), low_stock (optional), inventory_type ("customer" or "internal"), category (optional)',
          response: [{ id: 'xxx', sku: 'PROD-001', name: 'Product Name', quantity: 100, inventory_type: 'customer' }],
        },
        {
          method: 'POST',
          path: '/api/products',
          description: 'Create a new product',
          requiresAuth: true,
          body: {
            sku: 'PROD-001',
            name: 'Product Name',
            barcode: '123456',
            quantity: 100,
            warehouse_id: 'wh-xxx',
            reorder_point: 10,
            inventory_type: 'customer',
            category: 'spare_parts (only for internal)',
            supplier: 'Optional supplier',
            cost_price: 100.00,
            unit_of_measure: 'unit',
          },
        },
        {
          method: 'GET',
          path: '/api/products/:id',
          description: 'Get product by ID',
        },
        {
          method: 'GET',
          path: '/api/products/barcode/:barcode',
          description: 'Get product by barcode (used for scanning)',
        },
        {
          method: 'PATCH',
          path: '/api/products/:id',
          description: 'Update product',
          requiresAuth: true,
        },
        {
          method: 'DELETE',
          path: '/api/products/:id',
          description: 'Delete product',
          requiresAuth: true,
        },
      ],
    },
    {
      category: 'Orders',
      routes: [
        {
          method: 'GET',
          path: '/api/orders',
          description: 'Get all orders',
          params: 'status (optional), warehouse_id (optional)',
        },
        {
          method: 'POST',
          path: '/api/orders',
          description: 'Create a new order',
          requiresAuth: true,
          body: {
            order_number: 'ORD-001',
            platform: 'shopify',
            customer_name: 'John Doe',
            warehouse_id: 'wh-xxx',
            items: [{ product_id: 'prod-xxx', sku: 'PROD-001', name: 'Product', quantity: 2 }],
          },
        },
        {
          method: 'GET',
          path: '/api/orders/:id',
          description: 'Get order by ID',
        },
        {
          method: 'PATCH',
          path: '/api/orders/:id/status',
          description: 'Update order status',
          requiresAuth: true,
          body: { status: 'picking' },
        },
        {
          method: 'POST',
          path: '/api/orders/:id/pick',
          description: 'Confirm item picked',
          requiresAuth: true,
          body: { product_id: 'prod-xxx', quantity: 1 },
        },
        {
          method: 'POST',
          path: '/api/orders/:id/pack',
          description: 'Mark order as packed',
          requiresAuth: true,
        },
        {
          method: 'POST',
          path: '/api/orders/:id/ship',
          description: 'Ship order (deducts inventory and sends to OMS)',
          requiresAuth: true,
          body: { tracking_number: 'TRACK123', notes: 'Shipped via FedEx' },
        },
      ],
    },
    {
      category: 'Warehouses',
      routes: [
        {
          method: 'GET',
          path: '/api/warehouses',
          description: 'Get all warehouses',
        },
        {
          method: 'POST',
          path: '/api/warehouses',
          description: 'Create a new warehouse',
          requiresAuth: true,
          body: { name: 'Main Warehouse', code: 'WH001', address: '123 Street' },
        },
        {
          method: 'GET',
          path: '/api/warehouses/:id',
          description: 'Get warehouse by ID',
        },
        {
          method: 'DELETE',
          path: '/api/warehouses/:id',
          description: 'Delete warehouse',
          requiresAuth: true,
        },
      ],
    },
    {
      category: 'Internal Inventory',
      routes: [
        {
          method: 'GET',
          path: '/api/products?inventory_type=internal',
          description: 'Get all internal materials/spare parts',
          params: 'category (optional), warehouse_id (optional), low_stock (optional)',
        },
        {
          method: 'GET',
          path: '/api/stock-adjustments',
          description: 'Get stock adjustment history',
          params: 'product_id (optional)',
        },
        {
          method: 'POST',
          path: '/api/stock-adjustments',
          description: 'Record a stock adjustment (in/out/correction)',
          requiresAuth: true,
          body: {
            product_id: 'product-id',
            adjustment_type: 'in|out|adjustment',
            quantity: 5,
            reason: 'purchase|usage|damaged|lost|return|count_correction|transfer|other',
            notes: 'Optional notes',
            performed_by: 'John Doe',
          },
        },
        {
          method: 'GET',
          path: '/api/dashboard/internal-stats',
          description: 'Get internal inventory statistics',
          response: {
            total_internal_items: 25,
            low_stock_internal: 3,
            category_breakdown: { spare_parts: 10, consumables: 15 },
            total_adjustments: 45,
          },
        },
      ],
    },
    {
      category: 'Webhooks',
      routes: [
        {
          method: 'POST',
          path: '/api/webhooks/shopify',
          description: 'Receive orders from Shopify',
          requiresAuth: true,
          body: { order: '...' },
        },
        {
          method: 'POST',
          path: '/api/webhooks/woocommerce',
          description: 'Receive orders from WooCommerce',
          requiresAuth: true,
          body: { order: '...' },
        },
      ],
    },
    {
      category: 'Dashboard',
      routes: [
        {
          method: 'GET',
          path: '/api/dashboard/stats',
          description: 'Get dashboard statistics',
          response: {
            total_products: 100,
            low_stock_items: 5,
            pending_orders: 10,
            picking_orders: 3,
            packed_orders: 2,
          },
        },
      ],
    },
  ];

  const getMethodColor = (method) => {
    const colors = {
      GET: 'bg-blue-100 text-blue-800',
      POST: 'bg-green-100 text-green-800',
      PATCH: 'bg-yellow-100 text-yellow-800',
      DELETE: 'bg-red-100 text-red-800',
    };
    return colors[method] || 'bg-slate-100 text-slate-800';
  };

  return (
    <div className="space-y-6" data-testid="api-docs-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-slate-900">API Documentation</h1>
        <p className="text-slate-600 mt-2">Complete reference for the WMS API</p>
      </div>

      {/* Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Getting Started</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="font-semibold text-slate-900 mb-2">Base URL</h3>
            <code className="bg-slate-100 px-3 py-2 rounded text-sm block">
              {BACKEND_URL}/api
            </code>
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 mb-2">Authentication</h3>
            <p className="text-sm text-slate-600 mb-2">
              Most endpoints require authentication via API key. Include your API key in the request header:
            </p>
            <code className="bg-slate-900 text-slate-100 px-3 py-2 rounded text-sm block">
              X-API-Key: your_api_key_here
            </code>
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 mb-2">Response Format</h3>
            <p className="text-sm text-slate-600">
              All responses are in JSON format. Successful requests return a 200 status code.
              Errors return appropriate 4xx or 5xx status codes with error details.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Endpoints */}
      <Tabs defaultValue={endpoints[0].category} className="w-full">
        <TabsList className="flex flex-wrap h-auto">
          {endpoints.map((section) => (
            <TabsTrigger key={section.category} value={section.category}>
              {section.category}
            </TabsTrigger>
          ))}
        </TabsList>

        {endpoints.map((section) => (
          <TabsContent key={section.category} value={section.category} className="space-y-4">
            {section.routes.map((route, index) => (
              <Card key={index}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <Badge className={getMethodColor(route.method)}>{route.method}</Badge>
                    <code className="font-mono text-sm">{route.path}</code>
                    {route.requiresAuth && (
                      <Badge variant="outline" className="text-xs">
                        Requires API Key
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mt-2">{route.description}</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {route.params && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900 mb-1">Query Parameters</h4>
                      <code className="text-xs text-slate-600">{route.params}</code>
                    </div>
                  )}
                  {route.body && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900 mb-2">Request Body</h4>
                      <pre className="bg-slate-900 text-slate-100 p-3 rounded text-xs overflow-x-auto">
                        {JSON.stringify(route.body, null, 2)}
                      </pre>
                    </div>
                  )}
                  {route.response && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900 mb-2">Example Response</h4>
                      <pre className="bg-slate-900 text-slate-100 p-3 rounded text-xs overflow-x-auto">
                        {JSON.stringify(route.response, null, 2)}
                      </pre>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        ))}
      </Tabs>

      {/* Integration with OMS */}
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle>Integration with Your OMS</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-slate-700">
          <p>
            <strong>To connect this WMS with your existing Order Management System (OMS):</strong>
          </p>
          <ol className="list-decimal list-inside space-y-2 ml-2">
            <li>Create an API key in the API Settings page</li>
            <li>Configure your OMS to send orders to: <code className="bg-white px-1 py-0.5 rounded">POST {BACKEND_URL}/api/orders</code></li>
            <li>When orders are shipped, the WMS will call your OMS webhook (to be configured)</li>
            <li>Include the API key in the <code className="bg-white px-1 py-0.5 rounded">X-API-Key</code> header</li>
          </ol>
          <p className="text-xs text-slate-600 mt-4">
            Note: Please provide your OMS API documentation to complete the bidirectional integration.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
